<script setup>
import {computed, onUnmounted, ref, watch, watchEffect} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import { useProjectStore } from '@/stores/project'
import {ChevronRight, Folders, MapPin, MapPinOff} from 'lucide-vue-next'

import PlaceBreadcrumbs from './PlaceBreadcrumbs.vue'
import PlaceTreeBranch from './PlaceTreeBranch.vue'
import ProjectCard from './ProjectCard.vue'
import {buildPlaceTree, flattenPlaces, placeBranches, placeLabel, resolvePlaceLevel, UNLOCATED} from '@/lib/places/placeTree'
import {buildStaticMapUrl} from '@/utils/mapbox/staticMap'
import {useProjectPins} from '@/composables/useProjectPins'
import {usePlaceMapLevel} from '@/composables/usePlaceMapLevel'

const props = defineProps({
  projects: { type: Array, required: true },
  mode: { type: String, default: 'cards' },
  boundaries: { type: Object, default: () => ({}) },
  searchQuery: { type: String, default: '' },
  joinableIds: { type: Array, default: () => [] },
})
const emit = defineEmits(['join'])

const projectStore = useProjectStore()
const route = useRoute()
const router = useRouter()

const tree = computed(() => buildPlaceTree(props.projects))
const isJoinable = (project) => props.joinableIds.includes(project.project_id)

const slugs = computed(() => {
  const place = route.params.place
  if (!place) return []
  return Array.isArray(place) ? place : [place]
})

const level = computed(() => resolvePlaceLevel(tree.value, slugs.value))

const {pinnedFirst} = useProjectPins()
const levelProjects = computed(() => level.value.kind === 'level' ? pinnedFirst(level.value.projects) : [])
const places = computed(() => flattenPlaces(tree.value))

const placeRoute = (target) => target.length
  ? { name: 'OrgProjectPlaces', params: { org_id: route.params.org_id, place: target } }
  : { name: 'OrgProjects', params: { org_id: route.params.org_id } }

// An empty list means projects haven't arrived yet, not that the Place is gone.
watch(level, (value) => {
  if (value.kind === 'redirect' && props.projects.length) router.replace(placeRoute(value.slugs))
}, { immediate: true })

const outlines = ref({})

// Refetched only when a Place appears or disappears, not on every project list refresh.
const placeKeys = computed(() => places.value.map(tile => tile.slugs.join('/')).sort().join(','))

watch(placeKeys, async () => {
  outlines.value = await projectStore.fetchPlaceOutlines()
}, { immediate: true })

const {placeMapLevel} = usePlaceMapLevel()

watchEffect(() => {
  if (level.value.kind !== 'level') return
  placeMapLevel.value = {
    places: level.value.tiles
      .map(tile => ({ ...tile, key: tile.slugs.join('/'), geometry: outlines.value[tile.slugs.join('/')] }))
      .filter(place => place.geometry),
    projectIds: level.value.projects.map(project => project.project_id),
  }
})

onUnmounted(() => { placeMapLevel.value = null })

const tilePreview = (tile) => {
  const place = tile.slugs.join('/')
  const geometry = outlines.value[place]
  return geometry ? buildStaticMapUrl({ type: 'Feature', properties: { place }, geometry }, { mainPartsOnly: true }) : null
}

const levelLabel = (level) => level.charAt(0).toUpperCase() + level.slice(1)

const query = computed(() => props.searchQuery.trim().toLowerCase())

const matches = (name) => (name || '').toLowerCase().includes(query.value)

const OPEN_KEYS_STORAGE = 'openedPlaceKeys'
const readOpenKeys = () => {
  try {
    return JSON.parse(localStorage.getItem(OPEN_KEYS_STORAGE) || '{}')
  } catch {
    return {}
  }
}
const openKeys = ref(readOpenKeys())

// The current Place (the URL) and everything above it are always expanded, so
// switching between cards and tree keeps you where you were.
const expandedKeys = computed(() => {
  const keys = { ...openKeys.value }
  slugs.value.forEach((_, i) => { keys[slugs.value.slice(0, i + 1).join('/')] = true })
  return keys
})

const saveOpenKeys = (next) => {
  openKeys.value = next
  try {
    localStorage.setItem(OPEN_KEYS_STORAGE, JSON.stringify(next))
  } catch {
    // Expanded Places just aren't remembered.
  }
}

// Expanding a Place makes it the current one; collapsing the current Place (or
// one above it) moves the current Place up, so cards open where the tree left off.
const togglePlace = (key) => {
  const next = { ...openKeys.value }
  const current = slugs.value.join('/')
  if (!expandedKeys.value[key]) {
    next[key] = true
    saveOpenKeys(next)
    router.replace(placeRoute(key.split('/')))
    return
  }
  delete next[key]
  saveOpenKeys(next)
  if (current === key || current.startsWith(`${key}/`)) router.replace(placeRoute(key.split('/').slice(0, -1)))
}

const branches = computed(() => placeBranches(
  tree.value,
  query.value ? (p) => matches(p.project_name) : undefined,
))

const searchResults = computed(() => pinnedFirst(props.projects
  .filter(p => matches(p.project_name))
  .sort((a, b) => a.project_name.localeCompare(b.project_name))))
</script>

<template>
  <Teleport v-if="!query && level.kind === 'level' && level.crumbs.length > 1" to="#panel-leading" defer>
    <PlaceBreadcrumbs :crumbs="level.crumbs" :places="places" :current="slugs" :to-route="placeRoute" />
  </Teleport>

  <div v-if="mode === 'tree'" class="py-1">
    <PlaceTreeBranch
      v-for="branch in branches"
      :key="branch.key"
      :branch="branch"
      :open-keys="expandedKeys"
      :force-open="!!query"
      :joinable-ids="joinableIds"
      @toggle="togglePlace"
      @join="emit('join', $event)"
    />
    <div v-if="!branches.length" class="flex items-center justify-center py-8">No projects found</div>
  </div>

  <div v-else class="px-2 py-1">

    <template v-if="query">
      <div class="grid grid-cols-2 gap-2">
        <ProjectCard
          v-for="project in searchResults"
          :key="project.project_id"
          :project="project"
          :parent-name="placeLabel(project)"
          :boundary="boundaries[project.project_id] || null"
          :joinable="isJoinable(project)"
          @join="emit('join', $event)"
        />
      </div>
      <div v-if="!searchResults.length" class="flex items-center justify-center py-8">No projects found</div>
    </template>

    <template v-else-if="level.kind === 'level'">
      <div v-if="level.tiles.length || level.projects.length" class="grid grid-cols-2 gap-2">
        <router-link
          v-for="tile in level.tiles"
          :key="tile.slugs.join('/')"
          :to="placeRoute(tile.target)"
          class="group relative block pb-3"
        >
          <!-- Cards peeking out underneath (inside the card's own bottom padding, so the grid spacing stays
               the same as for project cards): a Place is a stack of projects, two layers once it holds several. -->
          <div
            v-if="tile.count > 1"
            class="absolute inset-x-4 bottom-0 top-3 rounded-lg border border-border bg-muted transition-colors group-hover:border-primary/40"
            aria-hidden="true"
          />
          <div
            class="absolute inset-x-2 bottom-1.5 top-1.5 rounded-lg border border-border bg-card transition-colors group-hover:border-primary/60"
            aria-hidden="true"
          />
          <div class="relative h-full rounded-lg border bg-card p-2 transition-colors group-hover:border-primary">
            <div class="relative aspect-video w-full overflow-hidden rounded-md bg-muted flex items-center justify-center">
              <span class="absolute top-1.5 left-1.5 z-10 flex items-center gap-1 rounded-full border bg-background/90 px-2 py-0.5 text-xs font-semibold">
                <Folders class="w-3.5 h-3.5" aria-hidden="true" />
                {{ tile.count }} {{ tile.count === 1 ? 'project' : 'projects' }}
              </span>
              <img
                v-if="tilePreview(tile)"
                :src="tilePreview(tile)"
                :alt="`Map of ${tile.name}`"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <MapPinOff v-else-if="tile.slugs[0] === UNLOCATED.slug" class="w-6 h-6 text-muted-foreground" aria-hidden="true" />
              <MapPin v-else class="w-6 h-6 text-muted-foreground" aria-hidden="true" />
            </div>
            <div class="mt-2 text-sm font-medium truncate" :title="tile.name">{{ tile.name }}</div>
            <div class="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPinOff v-if="tile.slugs[0] === UNLOCATED.slug" class="w-3 h-3 shrink-0" aria-hidden="true" />
              <MapPin v-else class="w-3 h-3 shrink-0" aria-hidden="true" />
              <span class="truncate">{{ tile.slugs[0] === UNLOCATED.slug ? 'No location' : levelLabel(tile.level) }}</span>
            </div>
          </div>
        </router-link>

        <ProjectCard
          v-for="project in levelProjects"
          :key="project.project_id"
          :project="project"
          :boundary="boundaries[project.project_id] || null"
          :joinable="isJoinable(project)"
          @join="emit('join', $event)"
        />
      </div>

      <div v-if="!level.tiles.length && !level.projects.length" class="flex items-center justify-center py-8">
        No projects found
      </div>
    </template>

    <div v-else-if="!projects.length" class="flex items-center justify-center py-8">No projects found</div>
  </div>
</template>
